import React, { useEffect } from "react";
import {
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import { useFormik } from "formik";
import { isEmpty, isEqual, isNull } from "lodash";

import { Search as SearchIcon } from "@material-ui/icons";
import DatePicker from "../../../../../components/base/date-picker";
import PageDomain, { WIDGET_TYPE } from "../../domain";
import PageContext from "../../context";
import { Skeleton } from "@material-ui/lab";
import MultiSelectFilterInput from "../../../../../components/admin/follow-ups/new-widget-follow-ups/components/multi-select-filter-input";
import { FilterModel, MODEL_FIELDS } from "../../model";
import { useDateRangeMeta } from "../../hook";

import WarningIcon from "@material-ui/icons/Warning";
import { useSelector } from "react-redux";

const WidgetTypeSelector = ({ loading = true }) => {
  const { widgetType, setWidgetType } = React.useContext(PageContext);

  const onClickItem = (event, item) => {
    event.preventDefault();
    if (item.id !== widgetType) {
      setWidgetType(item.id);
    }
  };

  return (
    <Grid container direction="row" className="widget-selector">
      <Typography className="widget-varaint-label">widget type</Typography>
      <Grid container direction="row" className="gap-x-16">
        {Object.values(WIDGET_TYPE).map((item) => {
          let cssClasses = "widget-selector-item";
          cssClasses += widgetType === item.id ? " selected" : "";
          return (
            <Typography
              className={cssClasses}
              onClick={(event) => onClickItem(event, item)}
            >
              {loading ? <Skeleton /> : item.label}
            </Typography>
          );
        })}
      </Grid>
    </Grid>
  );
};

const PackageWiseConversionFilters = ({ pricingTiers, setFieldValue }) => {
  return (
    <Grid container direction="column">
      <Typography className="widget-varaint-label">Filters</Typography>
      <MultiSelectFilterInput
        meta={FilterModel[MODEL_FIELDS.pricingTiers]}
        value={pricingTiers}
        onChange={(nextValue) => {
          const currentValue = pricingTiers;
          if (!isEqual(currentValue, nextValue)) {
            setFieldValue(MODEL_FIELDS.pricingTiers, nextValue);
          }
        }}
      />
    </Grid>
  );
};

const DepricatedDataInaccurateAlert = () => {
  return (
    <Typography
      variant="body2"
      className="border-4 gap-x-8 px-16 padding-block-4 w-fit old-operator-stats-results-alert"
    >
      <WarningIcon />
      Historical operator stats (before March 1, 2026) may contain
      inconsistencies. Daily stats recorded from March 1, 2026 onward are
      accurate.
    </Typography>
  );
};

const FilterSection = () => {
  const { pricingPackages } = useSelector((state) => state.genericDataV2);

  const {
    operators,
    widgetType,
    setRawApiData,
    setIsSubmitting,
    setFilterValues,
  } = React.useContext(PageContext);
  const {
    values,
    setFieldValue,
    submitForm,
    isSubmitting,
    errors,
    submitCount,
  } = useFormik({
    initialValues: {
      operators: [],
      fromDate: null,
      toDate: null,
      [FilterModel.pricingTiers.fieldName]:
        FilterModel.pricingTiers.options.map((item) => item.value),
    },
    onSubmit: (values, actions) => {
      PageDomain.onSubmitFilterForm({
        values,
        actions,
        operators,
        pricingPackages,
        setRawApiData,
      });
    },
    validate: PageDomain.validateFilterForm,
    validateOnMount: true,
  });

  const dateRangeMeta = useDateRangeMeta({
    fromDate: values.fromDate,
    toDate: values.toDate,
  });

  const initializing = isEmpty(operators) || isSubmitting;

  useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  useEffect(() => {
    setFilterValues(values);
  }, [setFilterValues, values]);

  return (
    <Grid container direction="column" className="filter-form-section">
      <Grid container direction="row" className="gap-x-16">
        <DatePicker
          label="From"
          selectedDate={values.fromDate}
          errorText={submitCount > 0 && errors.fromDate}
          minDate={dateRangeMeta.from.minDate}
          maxDate={dateRangeMeta.from.maxDate}
          onChangeDate={(date) => setFieldValue("fromDate", date)}
          loading={initializing}
        />
        <DatePicker
          label="To"
          selectedDate={values.toDate}
          errorText={submitCount > 0 && errors.toDate}
          minDate={dateRangeMeta.to.minDate}
          maxDate={dateRangeMeta.to.maxDate}
          onChangeDate={(date) => setFieldValue("toDate", date)}
          disabled={isNull(values.fromDate)}
          loading={initializing}
        />
        <IconButton
          className="search-icon-button"
          onClick={submitForm}
          disabled={initializing}
        >
          {initializing ? (
            <CircularProgress size={15} color="#000" />
          ) : (
            <SearchIcon />
          )}
        </IconButton>
      </Grid>
      {dateRangeMeta.useMonthlyTableStats && <DepricatedDataInaccurateAlert />}
      <WidgetTypeSelector loading={initializing} />
      {widgetType === WIDGET_TYPE.operatorPricingTierConvertions.id && (
        <PackageWiseConversionFilters
          pricingTiers={values[MODEL_FIELDS.pricingTiers]}
          setFieldValue={setFieldValue}
        />
      )}
      <Divider />
    </Grid>
  );
};

export default FilterSection;
